import { useState } from 'react';
import { format } from 'date-fns';
import { 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  Loader2,
  AlertCircle,
  Activity,
  Download,
  Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  useQueueStats,
  useQueueJobs,
  useOverdueJobs,
  useRetryFailedJobs
} from '@/hooks/useEmailBackend';
import type { QueueJob } from '@/lib/email/edge-functions-api';

interface QueueManagementPanelProps {
  campaignId?: string;
}

export function QueueManagementPanel({ campaignId }: QueueManagementPanelProps) {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'scheduled_at' | 'created_at' | 'status'>('scheduled_at');

  const { data: queueStats } = useQueueStats();
  const { data: allJobs } = useQueueJobs(campaignId);
  const { data: failedJobs } = useQueueJobs(campaignId, 'failed');
  const { data: overdueJobs } = useOverdueJobs();
  const retryJobs = useRetryFailedJobs();

  const stats = queueStats?.data;
  const jobs = allJobs?.data || [];
  const failed = failedJobs?.data || [];
  const overdue = overdueJobs?.data || [];

  const filteredJobs = jobs
    .filter(job => {
      if (statusFilter !== 'all' && job.status !== statusFilter) return false;
      if (searchTerm && !job.recipient_email.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'scheduled_at':
          return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
        case 'created_at':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });

  const handleRetryAll = () => {
    retryJobs.mutate(campaignId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'processing': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'retrying': return <RefreshCw className="h-4 w-4 text-yellow-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-gray-500" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'secondary';
      case 'failed': return 'destructive';
      case 'processing': return 'default';
      case 'retrying': return 'outline';
      case 'pending': return 'outline';
      default: return 'secondary';
    }
  };

  const getPriorityLevel = (job: QueueJob) => {
    const now = new Date();
    const scheduled = new Date(job.scheduled_at);
    const hoursDiff = (now.getTime() - scheduled.getTime()) / (1000 * 60 * 60);
    
    if (job.status === 'failed') return 'high';
    if (hoursDiff > 2) return 'medium';
    return 'low';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Queue Statistics Overview */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_jobs.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Processing</CardTitle>
              <Loader2 className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{stats.processing_jobs.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.pending_jobs.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Failed</CardTitle>
              <XCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.failed_jobs.toLocaleString()}</div>
              {stats.failed_jobs > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 w-full"
                  onClick={handleRetryAll}
                  disabled={retryJobs.isPending}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry All
                </Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.completed_jobs.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Avg: {stats.avg_processing_time}ms
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Queue Progress */}
      {stats && stats.total_jobs > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Queue Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Completion Rate</span>
                  <span>{((stats.completed_jobs / stats.total_jobs) * 100).toFixed(1)}%</span>
                </div>
                <Progress value={(stats.completed_jobs / stats.total_jobs) * 100} className="h-2" />
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Success Rate:</span>
                  <div className="font-medium">
                    {((stats.completed_jobs / (stats.completed_jobs + stats.failed_jobs || 1)) * 100).toFixed(1)}%
                  </div>
                </div>
                {stats.last_processed_at && (
                  <div>
                    <span className="text-muted-foreground">Last Processed:</span>
                    <div className="font-medium">
                      {format(new Date(stats.last_processed_at), 'MMM d, HH:mm')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts for Overdue Jobs */}
      {overdue.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <div className="font-medium">
              {overdue.length} overdue jobs detected
            </div>
            <div className="text-sm mt-1">
              Some email jobs are significantly past their scheduled time and may need attention.
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Detailed Job Management */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="jobs">All Jobs</TabsTrigger>
          <TabsTrigger value="failed">Failed Jobs</TabsTrigger>
          <TabsTrigger value="overdue">Overdue Jobs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Queue Health</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span>Processing Efficiency</span>
                    <Badge variant="secondary">
                      {stats ? ((stats.completed_jobs / (stats.total_jobs || 1)) * 100).toFixed(0) : 0}%
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Error Rate</span>
                    <Badge variant={stats && stats.failed_jobs > 0 ? 'destructive' : 'secondary'}>
                      {stats ? ((stats.failed_jobs / (stats.total_jobs || 1)) * 100).toFixed(1) : 0}%
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Queue Backlog</span>
                    <Badge variant={stats && stats.pending_jobs > 100 ? 'default' : 'secondary'}>
                      {stats?.pending_jobs || 0}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Issues</CardTitle>
              </CardHeader>
              <CardContent>
                {failed.slice(0, 3).map((job, index) => (
                  <div key={index} className="flex items-center gap-2 py-2 border-b last:border-b-0">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{job.recipient_email}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {job.error_message}
                      </div>
                    </div>
                  </div>
                ))}
                {failed.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No recent failures
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          {/* Filters and Search */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-4 items-center">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email address..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="retrying">Retrying</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled_at">Scheduled Time</SelectItem>
                    <SelectItem value="created_at">Created Time</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Jobs List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Email Jobs ({filteredJobs.length})</span>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredJobs.slice(0, 50).map((job) => {
                  const priority = getPriorityLevel(job);
                  return (
                    <div key={job.id} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(job.status)}
                        <div>
                          <div className="font-medium">{job.recipient_email}</div>
                          <div className="text-sm text-muted-foreground">
                            Scheduled: {format(new Date(job.scheduled_at), 'MMM d, HH:mm')}
                            {job.attempts > 1 && ` • Attempt ${job.attempts}/${job.max_attempts}`}
                          </div>
                          {job.error_message && (
                            <div className="text-xs text-red-600 mt-1">
                              Error: {job.error_message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={getPriorityColor(priority)} className="text-xs">
                          {priority.toUpperCase()}
                        </Badge>
                        <Badge variant={getStatusColor(job.status)}>
                          {job.status}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
                {filteredJobs.length > 50 && (
                  <div className="text-center py-2 text-sm text-muted-foreground">
                    Showing first 50 of {filteredJobs.length} jobs
                  </div>
                )}
                {filteredJobs.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No jobs match the current filters
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="failed" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Failed Jobs ({failed.length})</span>
                {failed.length > 0 && (
                  <Button 
                    onClick={handleRetryAll}
                    disabled={retryJobs.isPending}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry All Failed
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {failed.length > 0 ? (
                <div className="space-y-3">
                  {failed.map((job) => (
                    <div key={job.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{job.recipient_email}</div>
                          <div className="text-sm text-muted-foreground">
                            Attempt {job.attempts} of {job.max_attempts} • 
                            Scheduled: {format(new Date(job.scheduled_at), 'MMM d, HH:mm')}
                          </div>
                          {job.error_message && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                              <strong>Error:</strong> {job.error_message}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="destructive">Failed</Badge>
                          {job.attempts < job.max_attempts && (
                            <Badge variant="outline">Retryable</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <div>No failed jobs</div>
                  <div className="text-sm">All email jobs are processing successfully</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overdue" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Overdue Jobs ({overdue.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {overdue.length > 0 ? (
                <div className="space-y-3">
                  {overdue.map((job) => (
                    <div key={job.id} className="border border-red-200 rounded-lg p-4 bg-red-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium">{job.recipient_email}</div>
                          <div className="text-sm text-red-600">
                            Scheduled: {format(new Date(job.scheduled_at), 'MMM d, yyyy HH:mm')}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Overdue by: {Math.round((Date.now() - new Date(job.scheduled_at).getTime()) / (1000 * 60 * 60))} hours
                          </div>
                        </div>
                        <Badge variant="destructive">Overdue</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <div>No overdue jobs</div>
                  <div className="text-sm">All jobs are processing on schedule</div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}